import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, User, Camera, Mail, School, AtSign, FileText, Lock } from 'lucide-react';
import { doc, getDoc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../../firebase';

const personalDetailsCache = {
    data: null,
    isInitialized: false,
};

export const PersonalDetailsPage = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;
    const fileInputRef = useRef(null);

    const cached = personalDetailsCache.data || {};
    const [loading, setLoading] = useState(!personalDetailsCache.isInitialized);
    const [saving, setSaving] = useState(false);
    
    const [name, setName] = useState(cached.name || '');
    const [username, setUsername] = useState(cached.username || '');
    const [originalUsername, setOriginalUsername] = useState(cached.username || '');
    const [bio, setBio] = useState(cached.bio || '');
    const [email, setEmail] = useState(cached.email || '');
    const [college, setCollege] = useState(cached.college || '');
    const [photoURL, setPhotoURL] = useState(cached.photoURL || '');
    const [imageFile, setImageFile] = useState(null);
    const [originalData, setOriginalData] = useState({
        name: cached.name || '',
        username: cached.username || '',
        bio: cached.bio || '',
        photoURL: cached.photoURL || ''
    });

    useEffect(() => {
        const fetchUserData = async () => {
            if (!currentUser) return;
            try {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setName(data.name || '');
                    setUsername(data.username || '');
                    setOriginalUsername(data.username || '');
                    setBio(data.bio || '');
                    setEmail(data.email || '');
                    setCollege(data.college || '');
                    setPhotoURL(data.photoURL || '');
                    const fetchedData = {
                        name: data.name || '',
                        username: data.username || '',
                        bio: data.bio || '',
                        email: data.email || '',
                        college: data.college || '',
                        photoURL: data.photoURL || ''
                    };
                    setOriginalData({
                        name: data.name || '',
                        username: data.username || '',
                        bio: data.bio || '',
                        photoURL: data.photoURL || ''
                    });
                    personalDetailsCache.data = fetchedData;
                    personalDetailsCache.isInitialized = true;
                }
            } catch (err) {
                console.error("Failed to load user data:", err);
            } finally {
                setLoading(false);
            }
        };
        if (!personalDetailsCache.isInitialized) {
            fetchUserData();
        }
    }, [currentUser]);

    const handleImageSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
        }
    };

    const hasChanges = name !== originalData.name || 
                       username !== originalData.username || 
                       bio !== originalData.bio || 
                       imageFile !== null;

    const handleSave = async () => {
        if (!name.trim()) return toast.error('Please enter your name.');
        if (!username.trim()) return toast.error('You need a username!');
        if (username.length < 3) return toast.error('Username must be at least 3 characters.');
        
        setSaving(true);
        try {
            // Username Transaction
            if (username !== originalUsername) {
                const check = await getDoc(doc(db, 'usernames', username));
                if (check.exists()) {
                    setSaving(false);
                    return toast.error('That username is already taken. Try another.');
                }
                await setDoc(doc(db, 'usernames', username), { uid: currentUser.uid });
                if (originalUsername) {
                    await deleteDoc(doc(db, 'usernames', originalUsername));
                }
            }

            // Image Upload
            let finalPhotoURL = photoURL;
            if (imageFile) {
                const storageRef = ref(storage, `profile_pictures/${currentUser.uid}_${Date.now()}`);
                await uploadBytes(storageRef, imageFile);
                finalPhotoURL = await getDownloadURL(storageRef);
            }

            // Update User Doc
            const updatedDoc = {
                name: name.trim(),
                username: username.trim(),
                bio: bio.trim(),
                photoURL: finalPhotoURL
            };
            await updateDoc(doc(db, 'users', currentUser.uid), updatedDoc);

            setOriginalUsername(username.trim());
            setPhotoURL(finalPhotoURL);
            setImageFile(null);
            setOriginalData(updatedDoc);
            personalDetailsCache.data = { ...personalDetailsCache.data, ...updatedDoc };
            personalDetailsCache.isInitialized = true;

            toast.success('Profile updated successfully!');
        } catch (err) {
            console.error("Save error:", err);
            toast.error("Failed to update profile. Please try again.");
        } finally {
            setSaving(false);
        }
    };


    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-screen bg-[#0a0a0a]">
                <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
            </div>
        );
    }

    return (
        <main className="flex-1 w-full flex flex-col bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center justify-between px-4 h-[60px]">
                <div className="flex items-center">
                    <button onClick={() => navigate('/settings')} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
                        <ChevronLeft size={24} className="text-white" />
                    </button>
                    <h1 className="text-xl font-bold text-white">Personal Details</h1>
                </div>
                <button 
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className="bg-[#0BD9E5] text-black font-bold px-4 py-1.5 rounded-full hover:bg-[#0BD9E5]/80 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-20 sm:pb-0">
                {/* Photo Upload */}
                <div className="flex flex-col items-center mb-8">
                    <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                        <div className="w-24 h-24 rounded-full bg-[#111] border-2 border-[#333] overflow-hidden flex items-center justify-center">
                            {photoURL ? (
                                <img src={photoURL} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <User size={40} className="text-gray-500" />
                            )}
                        </div>
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Camera size={24} className="text-white" />
                        </div>
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleImageSelect} 
                            accept="image/*" 
                            className="hidden" 
                        />
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                        <span className="text-sm text-[#0BD9E5] font-medium cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                            Change Photo
                        </span>
                        {(photoURL || imageFile) && (
                            <button 
                                type="button" 
                                onClick={() => { setPhotoURL(''); setImageFile(null); }}
                                className="text-sm text-red-400 hover:text-red-300 font-medium transition-colors"
                            >
                                Remove Photo
                            </button>
                        )}
                    </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                    {/* Name */}
                    <div className="bg-[#111] border border-[#222] rounded-xl p-3 flex flex-col focus-within:border-[#0BD9E5] transition-colors">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <User size={12} /> Full Name
                        </label>
                        <input 
                            type="text" 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="bg-transparent border-none text-white text-[15px] outline-none"
                            placeholder="Your name"
                        />
                    </div>

                    {/* Username */}
                    <div className="bg-[#111] border border-[#222] rounded-xl p-3 flex flex-col focus-within:border-[#0BD9E5] transition-colors">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <AtSign size={12} /> Username
                        </label>
                        <input 
                            type="text" 
                            value={username}
                            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                            className="bg-transparent border-none text-white text-[15px] outline-none"
                            placeholder="username"
                            maxLength={20}
                        />
                    </div>

                    {/* Bio */}
                    <div className="bg-[#111] border border-[#222] rounded-xl p-3 flex flex-col focus-within:border-[#0BD9E5] transition-colors">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <FileText size={12} /> Bio
                        </label>
                        <textarea 
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            className="bg-transparent border-none text-white text-[15px] outline-none resize-none min-h-[80px]"
                            placeholder="A bit about yourself..."
                            maxLength={150}
                        />
                    </div>

                    {/* Email (Read Only) */}
                    <div className="bg-[#1a1a1a] border border-[#222] rounded-xl p-3 flex flex-col opacity-70 cursor-not-allowed">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <Mail size={12} /> Email <Lock size={12} className="ml-1 text-gray-500" />
                        </label>
                        <input 
                            type="text" 
                            value={email}
                            readOnly
                            className="bg-transparent border-none text-white text-[15px] outline-none cursor-not-allowed"
                        />
                    </div>

                    {/* College (Read Only) */}
                    <div className="bg-[#1a1a1a] border border-[#222] rounded-xl p-3 flex flex-col opacity-70 cursor-not-allowed">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <School size={12} /> College <Lock size={12} className="ml-1 text-gray-500" />
                        </label>
                        <input 
                            type="text" 
                            value={college}
                            readOnly
                            className="bg-transparent border-none text-white text-[15px] outline-none cursor-not-allowed"
                        />
                    </div>
                </div>
            </div>
        </main>
    );
};

export default PersonalDetailsPage;
